/**
 * Lot 2.5 — Clôture pédagogique : heures réellement dispensées et heures réalisées par apprenant, avec son assiduité.
 *
 * Repris du premier SaaS (`ClotureFinancementCard`) : source du BPF (heures-stagiaires, heures dispensées) et de
 * l'attestation de réalisation (indicateur 11). Par défaut, tout vient de l'émargement ; le formateur ne déclare que
 * l'écart (séance écourtée, feuille papier, abandon), et un écart exige un motif écrit, gardé au journal du dossier.
 * Module pur.
 */
import type { Resultat } from "../pedagogie/parcours";

export const ASSIDUITES = [
  { valeur: "assidu", libelle: "Assidu (toute la formation)" },
  { valeur: "partiel", libelle: "Présence partielle" },
  { valeur: "absent", libelle: "Absent" },
  { valeur: "abandon", libelle: "Abandon en cours de formation" },
] as const;
export type Assiduite = (typeof ASSIDUITES)[number]["valeur"];
const VALEURS = new Set<string>(ASSIDUITES.map((a) => a.valeur));

/** Heures retenues pour un apprenant : la déclaration de clôture si elle existe, sinon l'émargement. */
export const heuresRetenues = (emargees: number, declarees: number | null | undefined): number => (declarees === null || declarees === undefined ? emargees : declarees);

/** Assiduité proposée par défaut à partir des heures (le formateur peut la corriger). */
export function assiduiteDeduite(heures: number, dispensees: number): Assiduite {
  if (heures <= 0) return "absent";
  return heures + 0.01 >= dispensees ? "assidu" : "partiel";
}

export interface EntreeCloture {
  heures_dispensees: number;
  stagiaires: Array<{ stagiaire_id: string; heures: number; assiduite: string }>;
  motif?: string;
}

export interface ContexteCloture {
  /** Heures planifiées (séances) ou, à défaut, durée prévue. */
  heures_prevues: number;
  stagiaires: Array<{ stagiaire_id: string; heures_emargees: number }>;
}

export interface Cloture {
  heures_dispensees: number;
  stagiaires: Array<{ stagiaire_id: string; heures: number; assiduite: Assiduite; ecart: boolean }>;
  /** Vrai si une valeur s'écarte de l'émargement ou des heures prévues : un motif est alors exigé. */
  ecart: boolean;
}

const arrondi = (h: number) => Math.round(h * 100) / 100;

export function validerCloture(e: EntreeCloture, c: ContexteCloture): Resultat<Cloture> {
  const erreurs: string[] = [];
  const dispensees = arrondi(e.heures_dispensees);
  if (!Number.isFinite(dispensees) || dispensees <= 0 || dispensees > 2000) erreurs.push("Indiquez les heures réellement dispensées (entre 0 et 2 000).");
  const attendus = new Set(c.stagiaires.map((x) => x.stagiaire_id));
  const recus = new Set(e.stagiaires.map((x) => x.stagiaire_id));
  if (attendus.size !== recus.size || [...attendus].some((id) => !recus.has(id))) erreurs.push("La clôture doit porter sur chaque apprenant du dossier, et sur eux seuls.");
  const stagiaires = e.stagiaires.map((x, i) => {
    const heures = arrondi(x.heures);
    if (!Number.isFinite(heures) || heures < 0) erreurs.push(`Apprenant ${i + 1} : heures réalisées invalides.`);
    else if (heures > dispensees + 0.001) erreurs.push(`Apprenant ${i + 1} : ${heures} h réalisées, plus que les ${dispensees} h dispensées.`);
    if (!VALEURS.has(x.assiduite)) erreurs.push(`Apprenant ${i + 1} : assiduité inconnue.`);
    if (x.assiduite === "absent" && heures > 0) erreurs.push(`Apprenant ${i + 1} : un apprenant absent n'a réalisé aucune heure.`);
    if ((x.assiduite === "assidu" || x.assiduite === "partiel") && heures === 0) erreurs.push(`Apprenant ${i + 1} : aucune heure réalisée, choisissez « Absent ».`);
    const emargees = c.stagiaires.find((s) => s.stagiaire_id === x.stagiaire_id)?.heures_emargees ?? 0;
    return { stagiaire_id: x.stagiaire_id, heures, assiduite: x.assiduite as Assiduite, ecart: Math.abs(heures - emargees) > 0.001 };
  });
  const ecart = Math.abs(dispensees - arrondi(c.heures_prevues)) > 0.001 || stagiaires.some((x) => x.ecart);
  if (ecart && !e.motif?.trim()) erreurs.push("Des heures diffèrent de l'émargement ou du planning : indiquez le motif (séance écourtée, feuille papier, abandon…).");
  return erreurs.length > 0 ? { ok: false, erreurs } : { ok: true, valeur: { heures_dispensees: dispensees, stagiaires, ecart } };
}
